<template>
  <v-container fluid class="bg-light" :class="compact ? 'pa-3' : 'pa-4'">
    <!-- Header Especializado (oculto en modo compacto: el código ya se muestra en la página) -->
    <div v-if="!compact" class="glass-header mb-6">
      <div class="d-flex align-center flex-wrap justify-space-between w-full px-4 px-md-6 py-4 gap-2">
        <div class="d-flex align-center">
          <div class="header-icon-container mr-4">
            <v-icon color="primary" size="32">mdi-book-open-variant</v-icon>
          </div>
          <div>
            <h1 class="text-h6 text-md-h5 font-weight-black primary--text mb-0 tracking-tight">Detalle del Asiento</h1>
            <div class="d-flex align-center">
              <v-chip color="primary lighten-5" text-color="primary" x-small
                class="font-weight-black mr-2">CÓDIGO</v-chip>
              <span class="text-subtitle-1 font-weight-bold grey--text text--darken-2">{{ diary_book_code }}</span>
            </div>
          </div>
        </div>

        <!-- <div class="d-flex align-center">
          <v-chip v-if="is_period_open" color="success" label small class="mr-2 font-weight-bold px-4">
            <v-icon left x-small>mdi-lock-open-variant</v-icon> PERIODO ABIERTO
          </v-chip>
          <v-chip v-else color="error" label small class="mr-2 font-weight-bold px-4">
            <v-icon left x-small>mdi-lock</v-icon> PERIODO CERRADO
          </v-chip>
        </div> -->
      </div>
    </div>

    <!-- Totales compactos: una sola franja horizontal -->
    <v-row v-if="compact" dense class="mb-2">
      <v-col cols="12" md="4" class="d-flex">
        <div class="compact-tile main-amount flex-grow-1">
          <v-icon color="primary" small class="mr-2">mdi-cash-multiple</v-icon>
          <span class="text-caption font-weight-black primary--text mr-3">MONTO</span>
          <template v-if="amountsByCurrency.length > 0">
            <span v-for="entry in amountsByCurrency" :key="entry.currency_code" class="d-flex align-center mr-3">
              <v-chip x-small label color="primary lighten-5" text-color="primary" class="font-weight-black mr-1">
                {{ entry.currency_code }}
              </v-chip>
              <span class="text-subtitle-2 font-weight-black primary--text">{{ currencyFormat(entry.amount) }}</span>
            </span>
          </template>
          <span v-else class="text-subtitle-2 font-weight-black primary--text mr-3">{{ formatCurrency(total_amount) }}</span>
          <v-spacer></v-spacer>
          <span class="text-caption font-weight-bold grey--text text--darken-1">≈ {{ total_amount_base }} Base</span>
        </div>
      </v-col>
      <v-col cols="12" md="4" v-if="show_credits" class="d-flex">
        <div class="compact-tile flex-grow-1">
          <v-icon color="success" small class="mr-2">mdi-arrow-down-bold-circle</v-icon>
          <span class="text-caption font-weight-black success--text">TOTAL DÉBITO (BASE)</span>
          <v-spacer></v-spacer>
          <span class="text-subtitle-2 font-weight-black success--text">${{ currencyFormat(currentTotalDebito) }}</span>
        </div>
      </v-col>
      <v-col cols="12" md="4" v-if="show_credits" class="d-flex">
        <div class="compact-tile flex-grow-1">
          <v-icon color="error" small class="mr-2">mdi-arrow-up-bold-circle</v-icon>
          <span class="text-caption font-weight-black error--text">TOTAL CRÉDITO (BASE)</span>
          <v-chip v-if="currentTotalCredito.toFixed(2) != currentTotalDebito.toFixed(2)" x-small color="error"
            class="ml-2 font-weight-black">
            <v-icon left x-small>mdi-alert</v-icon> DESCUADRE
          </v-chip>
          <v-spacer></v-spacer>
          <span class="text-subtitle-2 font-weight-black error--text">${{ currencyFormat(currentTotalCredito) }}</span>
        </div>
      </v-col>
    </v-row>

    <!-- Sección de Totales Premium -->
    <v-row v-else class="mb-3">
      <!-- Card Monto Principal -->
      <v-col cols="12" md="4" class="d-flex">
        <v-card class="rounded-lg premium-card main-amount overflow-hidden flex-grow-1" elevation="0">
          <div class="card-glow"></div>
          <v-card-text class="pa-3">
            <div class="d-flex align-center mb-1">
              <v-icon color="primary" class="mr-1" x-small>mdi-cash-multiple</v-icon>
              <span class="text-caption font-weight-black primary--text tracking-widest">MONTO DE TRANSACCIÓN</span>
            </div>
            <div class="display-container">
              <template v-if="amountsByCurrency.length > 0">
                <div v-for="entry in amountsByCurrency" :key="entry.currency_code" class="d-flex align-center mb-1">
                  <v-chip x-small label color="primary lighten-5" text-color="primary" class="font-weight-black mr-2">
                    {{ entry.currency_code }}
                  </v-chip>
                  <span class="text-subtitle-1 font-weight-black primary--text">{{ currencyFormat(entry.amount) }}</span>
                </div>
              </template>
              <template v-else>
                <h2 class="text-subtitle-1 font-weight-black mb-1 primary--text">{{ formatCurrency(total_amount) }}</h2>
              </template>
              <div class="d-flex align-center mt-1">
                <v-icon color="grey darken-1" x-small class="mr-1">mdi-approximately-equal</v-icon>
                <span class="text-caption font-weight-bold grey--text text--darken-1">{{ total_amount_base }} Base</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Card Débito -->
      <v-col cols="12" md="4" v-if="show_credits" class="d-flex">
        <v-card class="rounded-lg premium-card balance-card flex-grow-1" elevation="0">
          <v-card-text class="pa-3 d-flex flex-column justify-center align-center h-full text-center">
            <v-avatar color="success lighten-5" size="32" class="mb-1">
              <v-icon color="success" small>mdi-arrow-down-bold-circle</v-icon>
            </v-avatar>
            <span class="text-caption font-weight-black success--text mb-1">TOTAL DÉBITO (BASE)</span>
            <span class="text-subtitle-1 font-weight-black success--text">
              ${{ currencyFormat(currentTotalDebito) }}
            </span>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Card Crédito -->
      <v-col cols="12" md="4" v-if="show_credits" class="d-flex">
        <v-card class="rounded-lg premium-card balance-card flex-grow-1" elevation="0">
          <v-card-text class="pa-3 d-flex flex-column justify-center align-center h-full text-center">
            <v-avatar color="error lighten-5" size="32" class="mb-1">
              <v-icon color="error" small>mdi-arrow-up-bold-circle</v-icon>
            </v-avatar>
            <span class="text-caption font-weight-black error--text mb-1">TOTAL CRÉDITO (BASE)</span>
            <span class="text-subtitle-1 font-weight-black error--text">
              ${{ currencyFormat(currentTotalCredito) }}
            </span>
          </v-card-text>

          <!-- Indicador de Descuadre en Card -->
          <div v-if="currentTotalCredito.toFixed(2) != currentTotalDebito.toFixed(2)" class="descuadre-v-tag">
            <v-icon left x-small color="white">mdi-alert</v-icon>
            DESCUADRE DETECTADO
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla de Detalles -->
    <v-card class="rounded-xl shadow-premium overflow-hidden border-light" elevation="0">
      <div class="d-flex flex-column flex-md-row align-start align-md-center justify-space-between px-4 py-3 gap-3 bg-white border-bottom">
        <div class="d-flex align-center mb-2 mb-md-0">
          <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
          <span class="font-weight-bold text-subtitle-1">Detalle de Movimientos</span>
        </div>
        
        <div class="d-flex flex-column flex-sm-row align-stretch align-sm-center gap-3 w-100" style="flex: 1; justify-content: flex-end;">
          <v-btn small depressed color="primary lighten-5"
            class="primary--text font-weight-bold text-none rounded-lg" @click="showGrouped = !showGrouped">
            <v-icon left small>mdi-group</v-icon>
            {{ showGrouped ? 'Ver Detalle' : 'Agrupar Cuentas' }}
          </v-btn>

          <v-text-field v-model="search" append-icon="mdi-magnify" label="Buscar en detalle..." single-line hide-details
            dense outlined class="modern-search-field rounded-lg" style="max-width: 400px;"></v-text-field>
        </div>
      </div>

      <div class="overflow-x-auto w-100">
        <v-data-table :height="displayedItems.length <= 30 ? 'auto' : '53vh'" :search="search"
          :disable-pagination="displayedItems.length <= 30" :hide-default-footer="displayedItems.length <= 30"
          fixed-footer fixed-header :headers="tableHeaders" :items="displayedItems" :items-per-page="20"
          class="modern-table no-elevation" :dense="displayedItems.length > 30" :footer-props="{
            itemsPerPageText: 'Líneas por página:',
            'items-per-page-options': opts
          }">

          <template v-slot:[`item.chart_of_account_name`]="{ item }">
            <div class="d-flex flex-column py-2">
              <span class="text-body-2 grey--text text--darken-3 font-weight-bold">{{ item.chart_of_account_name }}</span>
            </div>
          </template>

          <template v-slot:[`item.currency_code`]="{ item }">
            <v-chip x-small outlined :color="item.currency_rate && item.currency_rate != 1 ? 'primary' : 'grey'">
              {{ item.currency_code || 'USD' }}
            </v-chip>
          </template>

          <template v-slot:[`item.amount`]="{ item }">
            <span class="font-weight-black" :class="item.entry_type_id == 1 ? 'success--text' : 'error--text'">
              {{ currencyFormat(item.amount) }}
            </span>
          </template>

          <template v-slot:[`item.base_amount`]="{ item }">
            <v-tooltip bottom v-if="item.currency_rate && item.currency_rate != 1">
              <template v-slot:activator="{ on, attrs }">
                <v-chip x-small v-bind="attrs" v-on="on"
                  :color="item.entry_type_id == 1 ? 'green lighten-1' : 'red lighten-1'" text-color="white">
                  {{ currencyFormat(item.amount * (item.currency_rate || 1)) }}
                </v-chip>
              </template>
              <span>{{ currencyFormat(item.amount) }} × {{ item.currency_rate }} = {{ currencyFormat(item.amount * item.currency_rate) }}</span>
            </v-tooltip>
            <span v-else class="caption grey--text">—</span>
          </template>

          <template v-slot:[`item.debit`]="{ item }">
            <span class="success--text font-weight-black">
              {{ item.entry_type_id == 1 ? currencyFormat(item.amount) : '—' }}
            </span>
          </template>

          <template v-slot:[`item.credit`]="{ item }">
            <span class="error--text font-weight-black">
              {{ item.entry_type_id == 2 ? currencyFormat(item.amount) : '—' }}
            </span>
          </template>

          <template v-slot:[`item.entity_name`]="{ item }">
            <span class="text-body-2 grey--text text--darken-2 font-weight-medium">
              {{ item.entity_name || '—' }}
            </span>
          </template>

          <template v-slot:[`item.location_name`]="{ item }">
            <v-chip outlined small color="grey lighten-1" class="text-caption font-weight-bold">
              {{ getLocationName(item.location_id) }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon color="primary" @click="openEditDialog(item)" v-if="cant_edit_aconting">
              <v-icon size="18">mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon color="error" @click="removeAccount(item)" v-if="cant_edit_aconting">
              <v-icon size="18">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </div>

      <v-divider></v-divider>

      <v-card-actions class="pa-4 bg-light d-flex flex-column flex-md-row justify-center justify-md-space-between align-center gap-3 w-100">
        <v-btn depressed color="primary" class="font-weight-bold text-none px-6 rounded-lg w-100 w-md-auto" @click="adjustDecimals"
          v-if="(show_cuadre_debit || show_cuadre_credit) && cant_edit_aconting">
          <v-icon left>mdi-auto-fix</v-icon>
          Cuadrar decimales
        </v-btn>

        <v-spacer class="d-none d-md-flex"></v-spacer>

        <div v-if="currentTotalCredito.toFixed(2) != currentTotalDebito.toFixed(2)"
          class="d-flex align-center px-4 py-2 rounded-lg bg-error-light error--text font-weight-bold text-center">
          <v-icon color="error" class="mr-2" size="20">mdi-alert-decagram</v-icon>
          DESCUADRE DETECTADO (BASE): ${{ Math.abs(currentTotalCredito - currentTotalDebito).toFixed(2) }}
        </div>

        <v-btn v-if="cant_edit_aconting" color="success"
          class="font-weight-black text-none px-10 rounded-lg shadow-success w-100 w-md-auto" @click="save_all"
          :disabled="disabled_save" :loading="loading_save">
          <v-icon left>mdi-content-save</v-icon>
          Guardar Cambios
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="showEditDialog" max-width="600" width="90%">
      <v-card>
        <v-card-title>
          <v-spacer></v-spacer>
          {{
            isEditing ? "Editar" : "Agregar"
          }} linea
          <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text class="pb-0">
          <v-container>
            <v-row align="center" justify="center">
              <v-col cols="12" md="12">
                <v-autocomplete v-model="editForm.chart_of_account_id" :items="accounts"
                  label="Cuenta contable (Obligatorio)" dense hide-details outlined @change="onAccountChange" />
              </v-col>
            </v-row>
            <v-row align="center" justify="center">
              <v-col cols="12" md="12">
                <v-autocomplete v-model="editForm.category_id" :items="filteredCategories" label="Categoría (Opcional)"
                  item-text="name" item-value="id" dense hide-details outlined clearable
                  @change="editForm.subcategory_id = null"></v-autocomplete>
              </v-col>
            </v-row>
            <v-row align="center" justify="center">
              <v-col cols="12" md="12">
                <v-autocomplete v-model="editForm.subcategory_id" :items="filteredSubCategories"
                  label="Sub-Categoría (Opcional)" item-text="name" item-value="id" clearable dense hide-details
                  outlined no-data-text="Seleccione una categoria"></v-autocomplete>
              </v-col>
            </v-row>
            <v-row align="center" justify="center">
              <v-col cols="12" md="12">
                <v-autocomplete v-model="editForm.department_id" :items="itemsDepartment"
                  label="Departamento (Obligatorio)" item-text="text" item-value="value" dense hide-details
                  outlined></v-autocomplete>
              </v-col>
            </v-row>
            <v-row align="center" justify="center">
              <v-col cols="12" md="12">
                <v-autocomplete v-model="editForm.location_id" :items="itemsLocation" label="Location (Obligatorio)"
                  item-text="text" item-value="value" dense hide-details outlined></v-autocomplete>
              </v-col>
            </v-row>
            <v-row align="center" justify="center">
              <v-col cols="12" md="12">
                <v-select :items="[
                  { text: 'Débito', value: 1 },
                  { text: 'Crédito', value: 2 },
                ]" label="Tipo entrada" v-model="editForm.entry_type_id" item-text="text" item-value="value" dense
                  hide-details outlined />
              </v-col>
            </v-row>
            <v-row align="center" justify="center">
              <v-col cols="12" :md="isEditing ? 6 : 12">
                <v-text-field v-model.number="editForm.amount"
                  :label="`Monto ${isEditing ? '' : '(se dividirá por item)'}`" type="number" dense min="1" outlined
                  hide-details />
              </v-col>
              <v-col cols="12" md="6" v-if="isEditing">
                <!-- <v-text-field
                  :value="suggestedAmount"
                  label="Monto sugerido"
                  type="number"
                  dense min="1" hide-details
                  outlined readonly
                /> -->
              </v-col>
            </v-row>
            <v-row v-if="(currentTotalDebito).toFixed(2) != (total_amount).toFixed(2)" align="center" justify="center">
              <span>Monto {{ parseFloat(this.currentTotalDebito.toFixed(2)) - parseFloat(this.totalAmountBase.toFixed(2))
                < 0 ? 'sobrante' : 'faltante' }}: {{ this.totalAmountBase - currentTotalDebito < 0 ? '-' : '+' }}{{
                  Math.abs(this.totalAmountBase - currentTotalDebito) }}</span>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showEditDialog = false" color="error">Cancelar</v-btn>
          <v-btn color="primary" outlined @click="saveAccount">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import moment from 'moment';
import accountCategoryRules from "../../mixins/accountCategoryRules";
export default {
  mixins: [accountCategoryRules],
  props: {
    order_item_entries: Array,
    total_amount: Number,
    totalAmountBase: Number,
    accounting_date: String,
    diary_book_code: String,
    total_amount_base: String,
    cant_edit: Boolean,
    search_names: Boolean,
    show_credits: Boolean,
    base_currency_code: {
      type: String,
      default: 'USD',
    },
    business_unit_id: {
      type: Number,
      default: 0,
    },
    transaction_amounts: {
      type: Array,
      default: () => [],
    },
    // Oculta el encabezado "Detalle del Asiento" y muestra los totales en una franja delgada
    compact: Boolean,
  },
  data() {
    return {
      index_to_split: null,
      search: null,
      opts: [30, 60, 100, -1],
      edited_entries: [],
      showGrouped: false,
      showEditDialog: false,
      isEditing: false,
      editTargetKey: null,
      is_period_open: null,
      editForm: {
        chart_of_account_id: null,
        chart_of_account_code: "",
        chart_of_account_name: "",
        category_id: null,
        subcategory_id: null,
        location_id: null,
        department_id: 0,
        entry_type_id: 1,
        amount: 1,
      },
      categories: [],
      accounts: [],
      itemsDepartment: [],
      itemsLocation: [],
      line_to_edit: null,
      edited: false,
      loading_save: false
    };
  },
  watch: {
    order_item_entries(newValue) {
      // 1️⃣ Actualizar montos y marcar los que siguen existiendo
      const existingIds = [];
      for (let key in this.edited_entries) {
        const line = newValue.find(entry => entry?.entry_id === this.edited_entries[key]?.entry_id);
        if (line) {
          // Si existe, actualizo el monto
          this.edited_entries[key].amount = line.amount;
          existingIds.push(this.edited_entries[key].entry_id);
        }
      }

      // 2️⃣ Agregar nuevos que no estén en edited_entries
      newValue.forEach(entry => {
        const exists = this.edited_entries.some(e => e?.entry_id === entry?.entry_id);
        if (!exists) {
          for (let item of entry.order_chart_of_accounts) {
            let selected = { code: '0', name: 'NO DEFINIDO' };
            if (this.search_names) {
              selected = this.find_name(this.accounts, item.chart_of_account_id);
              if (selected == undefined) {
                return
              }
            }
            this.edited_entries.push({
              unique_id: this.generateUniqueId(),
              entry_id: entry.entry_id,
              chart_of_account_id: item.chart_of_account_id,
              chart_of_account_code: item.chart_of_account_code || selected.code,
              chart_of_account_name: `${item.chart_of_account_code || selected.code} - ${item.chart_of_account_name || selected.name}`,
              category_id: item.category_id,
              subcategory_id: item.subcategory_id,
              location_id: item.location_id || null,
              department_id: item.department_id || 0,
              department_name: this.getDepartmentName(item.department_id),
              category_name: this.getCategoryName(item.category_id),
              subcategory_name: this.getCategoryName(item.subcategory_id),
              entry_type_id: item.entry_type_id,
              amount: item.amount,
              currency_id: item.currency_id,
              currency_code: item.currency_code || entry.currency_code || 'USD',
              currency_rate: item.currency_rate,
              diary_book_id: item.diary_book_id,
              entity_id: item.entity_id,
              entity_name: entry.entity_name || item.entity_name || null,
            });
          }
        }
      });

      // 3️⃣ Eliminar los que ya no estén en newValue
      this.edited_entries = this.edited_entries.filter(e =>
        newValue.some(nv => nv.entry_id === e.entry_id)
      );
    }
  },
  computed: {
    amountsByCurrency() {
      if (this.transaction_amounts && this.transaction_amounts.length > 0) {
        const map = {};
        for (const item of this.transaction_amounts) {
          if (item.entry_type_id !== 1) continue;
          const code = item.currency_code || 'USD';
          if (!map[code]) map[code] = { currency_code: code, amount: 0 };
          map[code].amount += parseFloat(item.amount) || 0;
        }
        return Object.values(map);
      }
      // Fallback: derivar desde edited_entries (ya tiene currency_code resuelto)
      const map = {};
      for (const entry of (this.edited_entries || [])) {
        if (entry.entry_type_id !== 1) continue;
        const code = entry.currency_code || 'USD';
        if (!map[code]) map[code] = { currency_code: code, amount: 0 };
        map[code].amount += parseFloat(entry.amount) || 0;
      }
      return Object.values(map);
    },
    cant_edit_aconting() {
      if (this.cant_edit == false) {
        return false
      }
      if (this.is_period_open == null || this.is_period_open == false) {
        return false
      }
      return true
    },
    show_cuadre_debit() {
      const dif = parseFloat(Math.abs(this.currentTotalDebito).toFixed(6)) - parseFloat(this.totalAmountBase.toFixed(6));
      if (Math.abs(dif) > 0.001 && Math.abs(dif) <= 0.5) {
        return true
      }
      return false;
    },
    show_cuadre_credit() {
      const dif = parseFloat(Math.abs(this.currentTotalCredito).toFixed(6)) - parseFloat(this.totalAmountBase.toFixed(6));
      if (Math.abs(dif) > 0.001 && Math.abs(dif) <= 0.5) {
        return true
      }
      return false;
    },
    suggestedAmount() {
      if (!this.isEditing) return "";
      const amountToMatch = this.totalAmountBase - (parseFloat(Math.abs(this.currentTotalDebito).toFixed(6)) - parseFloat(this.totalAmountBase.toFixed(6)));
      return amountToMatch//.toFixed(2);
    },

    currentTotalDebito() {
      return parseFloat(
        this.edited_entries.reduce(
          (acc, line) => acc + ((line.entry_type_id == 1) ? (line.amount * (line.currency_rate || 1)) : 0),
          0
        )
      );
    },
    currentTotalCredito() {
      return parseFloat(
        this.edited_entries.reduce(
          (acc, line) => acc + ((line.entry_type_id == 2) ? (line.amount * (line.currency_rate || 1)) : 0),
          0
        )
      );
    },
    displayedItems() {
      if (this.showGrouped) {
        const groups = {};
        this.edited_entries.forEach((line) => {
          const key = `${line.chart_of_account_id}|${line.category_id}|${line.subcategory_id}|${line.department_id}|${line.location_id}|${line.entry_type_id}`;
          if (!groups[key]) {
            groups[key] = { ...line, amount: 0 };
          }
          groups[key].amount += line.amount;
        });
        return Object.values(groups);
      } else {
        return this.edited_entries;
      }
    },
    tableHeaders() {
      const base = [
        { text: "Cuenta", value: "chart_of_account_name" },
        { text: "Categoría", value: "category_name" },
        { text: "Subcategoría", value: "subcategory_name" },
        { text: "Entidad", value: "entity_name" },
        { text: "Location", value: "location_name" },
        { text: "Departamento", value: "department_name" },
        { text: "Moneda", value: "currency_code", align: "center" },
        { text: "Monto", value: "amount", align: "end" },
        { text: "Monto (Base)", value: "base_amount", align: "end" },
        { text: "Debito", value: "debit", align: " d-none" },
        { text: "Credito", value: "credit", align: " d-none" },
        { text: "Acciones", value: "actions", sortable: false, align: "end" },
      ];
      if (!this.cant_edit_aconting) {
        base[11].align = " d-none";
      }
      if (this.show_credits) {
        base[7].align = " d-none";
        base[9].align = "end";
        base[10].align = "end";
      }
      return base;
    },
    filteredCategories() {
      return this.categoriesForAccount(this.categories, this.editForm.chart_of_account_id);
    },
    filteredSubCategories() {
      if (this.editForm.category_id == null) {
        return [];
      }
      return this.subcategoriesForAccount(this.categories, this.editForm.chart_of_account_id, this.editForm.category_id);
    },
    disabled_save() {
      // Redondea los valores a 2 decimales para la comparación.
      const total_amountRounded = parseFloat(this.totalAmountBase.toFixed(2));
      const currentTotalDebitoRounded = parseFloat(this.currentTotalDebito.toFixed(2));
      const currentTotalCreditoRounded = parseFloat(this.currentTotalCredito.toFixed(2));

      // Deshabilita si los totales no cuadran.
      if (currentTotalDebitoRounded !== total_amountRounded) {
        return true;
      }
      if (currentTotalCreditoRounded !== currentTotalDebitoRounded) {
        return true;
      }
      // Deshabilita si no hay cambios.
      if (!this.edited) {
        return true;
      }

      // Permite guardar si todo cuadra.
      return false;
    },
  },
  created() {
    this.loadCatalogs();
    this.get_calendar();
  },
  methods: {
    async loadCatalogs() {
      const categoriesRes = await this.api_Get(
        "accounting/categories_diary_book"
      );
      this.categories = await categoriesRes.json();
      const accountsRes = await this.api_Get("accounting/accountsUsable");
      let accounts = await accountsRes.json();
      for (let key in accounts) {
        if (accounts[key].is_system_only) {
          continue;
        }
        accounts[key].text = `${accounts[key].code} - ${accounts[key].name}`;
        accounts[key].value = accounts[key].id;
        this.accounts.push(accounts[key]);
      }
      this.itemsDepartment = await this.g_departments();
      this.itemsDepartment.unshift({ value: 0, text: "Todos" })
      for (let key in this.itemsDepartment) {
        this.itemsDepartment[key].text = `${this.itemsDepartment[key].text} (${this.itemsDepartment[key].value})`;
      }
      this.itemsLocation = Object.assign([], this.$store.state.auth.locations.filter(element => this.business_unit_id > 0 ? element.bus_id === this.business_unit_id : true));

      this.initializeEditedEntries();
    },
    getCategoryName(id) {
      const found = this.categories.find((cat) => cat.id === id);
      return found ? found.name : id;
    },
    getDepartmentName(id) {
      if (id == 0) {
        return "TODOS (0)";
      }
      const found = this.itemsDepartment.find((dep) => dep.value === id);
      return found ? `${found.text}` : id;
    },
    getLocationName(id) {
      const found = this.itemsLocation.find((loc) => loc.value === id);
      return found ? `${found.text} (${found.desc_loc_short})` : id;
    },
    find_name(array, id) {
      return array.find((item) => item.id === id);
    },
    onAccountChange(id) {
      const selected = this.find_name(this.accounts, id);
      if (selected) {
        this.editForm.chart_of_account_code = selected.code;
        this.editForm.chart_of_account_name = `${selected.code} - ${selected.name}`;
      }
      this.resetInvalidAccountCategory(this.editForm, this.categories, id, 'category_id', 'subcategory_id');
    },
    initializeEditedEntries() {
      this.order_item_entries.forEach((entry) => {
        entry.order_chart_of_accounts.forEach((account) => {
          let selected = { code: '0', name: 'NO DEFINIDO' };
          if (this.search_names) {
            selected = this.find_name(this.accounts, account.chart_of_account_id);
          }
          this.edited_entries.push({
            unique_id: this.generateUniqueId(),
            entry_id: entry.entry_id,
            chart_of_account_id: account.chart_of_account_id,
            chart_of_account_code: account.chart_of_account_code,
            chart_of_account_name: `${account.chart_of_account_code || selected.code} - ${account.chart_of_account_name || selected?.name}`,
            category_id: account.category_id,
            subcategory_id: account.subcategory_id,
            location_id: account.location_id || null,
            department_id: account.department_id || 0,
            department_name: this.getDepartmentName(account.department_id),
            category_name: this.getCategoryName(account.category_id),
            subcategory_name: this.getCategoryName(account.subcategory_id),
            entry_type_id: account.entry_type_id,
            currency_id: account.currency_id,
            currency_code: account.currency_code || entry.currency_code || 'USD',
            diary_book_id: account.diary_book_id,
            currency_rate: account.currency_rate,
            entity_id: account.entity_id,
            entity_name: entry.entity_name || account.entity_name || null,
            amount: account.amount //entry.price * entry.quantity,
          });
        });
      });
    },
    formatCurrency(value) {
      const safeValue = (value == null || isNaN(value)) ? 0 : value;
      // Usar base_currency_code (moneda de la unidad de negocio) ya que total_amount está en moneda base
      return `${this.base_currency_code}$${this.currencyFormat(safeValue)}`;
    },
    currencyFormat(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    getAmountColor(entryTypeId) {
      if (entryTypeId === 1) return "green";
      if (entryTypeId === 2) return "red";
      return "inherit";
    },
    openEditDialog(item) {
      this.isEditing = true;
      this.line_to_edit = item;
      this.editTargetKey = `${item.chart_of_account_id}|${item.category_id}|${item.subcategory_id}|${item.location_id}|${item.department_id}|${item.entry_type_id}`;

      this.editForm = this.showGrouped ? {
        ...item,
        amount: this.getGroupAmount(this.editTargetKey),
      } : item;
      this.on_off_show_all_items();
      this.showEditDialog = true;
    },
    on_off_show_all_items() {
      if (this.itemsLocation.length == 1) {//SI EL USUARIO SOLO TIENE ACCESO A UNA LOCATION LA ASIGNA POR DEFECTO
        this.editForm.location_id = this.itemsLocation[0].value;
      }
      if (this.showGrouped) {
        return
      }
    },
    openAddDialog() {
      this.isEditing = false;
      this.editForm = {
        chart_of_account_id: null,
        chart_of_account_code: "",
        chart_of_account_name: "",
        category_id: null,
        subcategory_id: null,
        location_id: null,
        department_id: 0,
        entry_type_id: 1,
        amount: 0,
      };
      this.on_off_show_all_items();
      this.showEditDialog = true;
    },
    getGroupAmount(key) {
      return this.edited_entries
        .filter((line) => this.getKey(line) === key)
        .reduce((acc, line) => acc + line.amount, 0);
    },
    getKey(line) {
      return `${line.chart_of_account_id}|${line.category_id}|${line.subcategory_id}|${line.location_id}|${line.department_id}|${line.entry_type_id}`;
    },
    saveAccount() {
      if (this.editForm.amount <= 0) {
        this.$toast.warning("El monto debe ser mayor a 0");
        return;
      }
      if (this.editForm.chart_of_account_id == null) {
        this.$toast.warning("Debe seleccionar una cuenta contable");
        return;
      }
      if (this.editForm.location_id == null) {
        this.$toast.warning("Debe seleccionar un location");
        return;
      }
      const categoryError = this.accountCategoryError(
        this.categories,
        this.editForm.chart_of_account_id,
        this.editForm.category_id,
        this.editForm.subcategory_id,
        this.editForm.chart_of_account_name
      );
      if (categoryError) {
        this.$toast.warning(categoryError);
        return;
      }
      if (this.editForm.category_id != null && this.editForm.subcategory_id == null) {
        this.$toast.warning("Debe seleccionar una subcategoría");
        return;
      }
      if (this.isEditing) {
        let originalLines = [];
        if (this.showGrouped) {
          originalLines = this.edited_entries.filter((line) => this.getKey(line) === this.editTargetKey);
        } else {
          originalLines = this.edited_entries.filter((line) => line === this.line_to_edit);
        }
        const distributedAmount = this.editForm.amount / originalLines.length;

        originalLines.forEach((line) => {
          Object.assign(line, {
            ...this.editForm,
            entry_id: line.entry_id,
            unique_id: this.generateUniqueId(),
            category_name: this.getCategoryName(this.editForm.category_id),
            subcategory_name: this.getCategoryName(this.editForm.subcategory_id),
            department_name: this.getDepartmentName(this.editForm.department_id),
            amount: distributedAmount
          });
        });
      } else {
        if (this.index_to_split != null) {

          const new_line = this.editForm;
          const old_line = this.edited_entries[this.index_to_split];

          const propsToCheck = [
            "chart_of_account_id",
            "category_id",
            "subcategory_id",
            "location_id",
            "department_id",
            "entry_type_id",
          ];

          const hasDifference = propsToCheck.some(key => old_line[key] !== new_line[key]);

          if (!hasDifference) {
            this.$toast.warning("La nueva linea debe tener al menos un campo diferente a la linea original");
            return
          }

          const rest_amunt_line = this.edited_entries[this.index_to_split].amount - this.editForm.amount;
          if (rest_amunt_line <= 0) {
            this.$toast.warning("El monto de esta linea no puede ser igual o mayor al monto de la linea original");
            return;
          }
          this.edited_entries[this.index_to_split].amount = rest_amunt_line;
          this.index_to_split = null;
        }

        this.edited_entries.push({
          unique_id: this.generateUniqueId(),
          chart_of_account_id: this.editForm.chart_of_account_id,
          chart_of_account_code: this.editForm.chart_of_account_code,
          chart_of_account_name: this.editForm.chart_of_account_name,
          category_id: this.editForm.category_id,
          subcategory_id: this.editForm.subcategory_id,
          location_id: this.editForm.location_id,
          department_id: this.editForm.department_id,
          department_name: this.getDepartmentName(this.editForm.department_id),
          category_name: this.getCategoryName(this.editForm.category_id),
          subcategory_name: this.getCategoryName(this.editForm.subcategory_id),
          entry_type_id: this.editForm.entry_type_id,
          amount: this.editForm.amount,
          diary_book_id: this.order_item_entries[0]?.diary_book_id || null,
          currency_rate: this.order_item_entries[0]?.currency_rate || 1,
          currency_id: this.order_item_entries[0]?.currency_id || 1,
          entity_id: this.order_item_entries[0]?.entity_id || null,
          entity_name: this.order_item_entries[0]?.entity_name || null,
        });
        // }
      }
      this.showEditDialog = false;
      this.emit_edited_accounts();
    },
    removeAccount(item) {
      this.emit_edited_accounts();
      if (this.showGrouped) {
        const key = this.getKey(item);
        this.edited_entries = this.edited_entries.filter((line) => this.getKey(line) !== key);
        return
      }
      const index = this.edited_entries.findIndex((line) => line === item);
      this.edited_entries.splice(index, 1);
    },
    async save_all() {
      if (Math.abs(this.currentTotalDebito).toFixed(2) != (this.totalAmountBase).toFixed(2)) {
        this.$toast.warning("El monto actual no coincide con el monto total de la orden");
        return;
      }

      this.loading_save = true;
      try {
        const response = await this.api_Post(
          "accounting/update_post_accounting",
          {
            accounting_detail: this.edited_entries,
            diary_book_id: this.edited_entries[0]?.diary_book_id || null,
          }
        );

        if (response.status == 200) {
          this.$toast.success("Contabilidad actualizada con éxito!");
        } else {
          const errorData = await response.json();
          this.$toast.error("Error: " + errorData.message);
        }
      } catch (error) {
        console.error("Error en la solicitud:", error.message);
      } finally {
        this.loading_save = false;
      }


      this.$emit("set_accounts", this.edited_entries);
      this.edited = false;
    },
    emit_edited_accounts() {
      this.edited = true;
      this.$emit("emit_edited_accounts", this.edited_entries);
    },
    copyLine(item) {
      this.editForm = { ...item };
      this.isEditing = false;
      this.showEditDialog = true;
    },
    splitLine(item) {
      const originalAmount = item.amount;
      this.index_to_split = this.edited_entries.findIndex((element) => element.unique_id == item.unique_id);
      // this.edited_entries[index].amount = originalAmount / 2;

      this.editForm = { ...item, amount: originalAmount / 2 };
      this.isEditing = false;
      this.showEditDialog = true;
    },
    adjustDecimals() {
      // Calcula la diferencia con la mayor precisión posible.
      const debit_difference = this.totalAmountBase - this.currentTotalDebito;
      const credit_difference = this.totalAmountBase - this.currentTotalCredito;

      const adjustment_threshold = 0.001; // Umbral de medio centavo.

      // Ajusta si la diferencia es mayor que el umbral.

      if (Math.abs(debit_difference) > adjustment_threshold) {
        for (let key in this.edited_entries) {
          if (this.edited_entries[key].entry_type_id == 1) {
            // Suma la diferencia y redondea el resultado a 2 decimales.
            this.edited_entries[key].amount += debit_difference; //parseFloat((this.edited_entries[key].amount + debit_difference).toFixed(2));
            break;
          }
        }
      }

      if (Math.abs(credit_difference) > adjustment_threshold) {
        for (let key in this.edited_entries) {
          if (this.edited_entries[key].entry_type_id == 2) {
            // Resta la diferencia y redondea el resultado a 2 decimales.
            this.edited_entries[key].amount += credit_difference; //parseFloat((this.edited_entries[key].amount - credit_difference).toFixed(2));
            break;
          }
        }
      }

      this.edited = true;
    },
    generateUniqueId() {
      const timestamp = new Date().getTime();
      const random = Math.floor(Math.random() * 10000);
      return `${timestamp}${random}`;
    },
    async get_calendar() {
      const request = await this.api_Post("accounting/get_calendar_accounting", {
        business_unit: this.business_unit_id,
        module: 4,
      });
      if (request.status == 200) {
        const data = await request.json();
        if (data.length > 0) {
          this.is_period_open = moment(this.accounting_date).isBetween(moment(data[0].from_date), moment(data[0].to_date), undefined, "[]");
        }
      }
    }
  },
};
</script>

<style scoped>
.v-data-table {
  font-size: 0.9rem;
}

.bg-light {
  background-color: #f8fafc !important;
}

.glass-header {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
}

.header-icon-container {
  background: #f1f5f9;
  padding: 12px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.premium-card {
  background: white;
  border: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.premium-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.1);
}

.main-amount {
  background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
  border-left: 6px solid #1976d2;
}

.compact-tile {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
}

.card-glow {
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100px;
  background: radial-gradient(circle, rgba(25, 118, 210, 0.05) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(30%, -30%);
}

.descuadre-v-tag {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #ff5252;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 900;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(255, 82, 82, 0.3);
  animation: pulse-red 2s infinite;
}

@keyframes pulse-red {
  0% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.shadow-premium {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

.border-light {
  border: 1px solid #e2e8f0 !important;
}

.modern-table {
  background-color: transparent !important;
}

.modern-table ::v-deep th {
  background-color: #f8fafc !important;
  color: #64748b !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  font-weight: 800 !important;
  font-size: 11px !important;
  height: 48px !important;
}

.modern-table ::v-deep td {
  height: 64px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.bg-error-light {
  background-color: #ffebf0;
}

.shadow-success {
  box-shadow: 0 10px 15px -3px rgba(76, 175, 80, 0.2), 0 4px 6px -2px rgba(76, 175, 80, 0.1) !important;
}

.tracking-tight {
  letter-spacing: -0.025em;
}

.tracking-widest {
  letter-spacing: 0.1em;
}

.modern-search-field ::v-deep .v-input__control {
  min-height: 40px !important;
}

.modern-search-field ::v-deep fieldset {
  border-color: #e2e8f0 !important;
}

.no-elevation {
  box-shadow: none !important;
}
</style>